/**
 * RecordPaymentDialog — collect a (possibly partial) payment against a single
 * plan-schedule period: bank amount, mode, date/time, proof-of-payment
 * upload, "Apply held deposit" / "Apply credit note" quick-actions, and
 * Mark unpaid.
 *
 * Extracted from account-detail.tsx (Phase 34/36 code — was a private,
 * non-exported function at lines 3754-4196) so a second surface — the
 * Member profile's "Collect payment" action (Phase 38) — can reuse it
 * without duplicating money-math. account-detail.tsx's own two call sites
 * are UNCHANGED behaviorally: they simply import this instead of defining it
 * locally, and never pass `additionalPeriods`.
 *
 * Phase 38 addition: an optional `additionalPeriods` prop. When the caller
 * (the member profile's MemberCollectPayment wrapper) passes a member's
 * OTHER non-Paid periods alongside the primary (oldest) one, this dialog
 * captures the payment metadata (mode/date/proof) ONCE via the existing
 * form and, right after the primary period settles, marks every additional
 * period fully paid (oldest-first, same metadata, all-or-report) — so ONE
 * Collect action clears the member's ENTIRE outstanding, not just the
 * oldest period. When `additionalPeriods` is unset/empty, behavior is
 * IDENTICAL to the original single-period dialog.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  useMarkPlanPeriodPaid,
  useUpdatePlanPeriodReminder,
  useApplyDepositToPeriod,
  useApplyCreditNoteToPeriod,
  getGetAccountPlanScheduleQueryKey,
  getListAccountCreditNotesQueryKey,
  type PlanSchedulePeriod,
  type HeldDeposit,
  type CreditNote,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { uploadPaymentProof } from "@/lib/upload";
import { Loader2, Upload } from "lucide-react";
import { apiErrorDetail } from "@/lib/period-payable";

// Re-declared locally — also defined in account-detail.tsx (still used there
// by GroupRecordPaymentDialog / ChargeRecordPaymentDialog, which stay in that
// file). Tiny, dependency-free constant/helpers; duplicating avoids a
// cross-module import back into a page file (mirrors the existing convention
// in components/members/member-plan-cards.tsx and components/billing/
// credit-notes-list.tsx, which both re-declare their own formatINR).
const PAYMENT_MODES = ["Cash", "UPI", "Bank_Transfer", "Card", "Cheque"] as const;

/** INR formatter — mirrors account-detail.tsx's formatINR. */
function formatINR(amount: number): string {
  if (!Number.isFinite(amount)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(amount)}`;
}

/** 2 dp rounding — money-correctness helper (mirrors account-detail.tsx). */
function round2(n: number): number {
  return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}

// Record a (possibly partial) payment against a period: amount received now,
// mode, date/time, and an optional proof-of-payment upload.
export function RecordPaymentDialog({
  accountId,
  period: initialPeriod,
  firstPeriodDeposit = 0,
  heldDeposits = [],
  creditNotes = [],
  additionalPeriods = [],
  onClose,
}: {
  accountId: number;
  period: PlanSchedulePeriod;
  /** One-time refundable security deposit billed on this (first) period, if any.
   *  Shown so the dialog's "Total to collect" reconciles with the receipt
   *  (rent + deposit). The recorded period payment stays rent-only; the deposit
   *  is held separately (member_security_deposits). */
  firstPeriodDeposit?: number;
  /** Account's Held security deposits, offered as a funding source toward
   *  this period's rent due (Quick task 9 — Apply held deposit). */
  heldDeposits?: HeldDeposit[];
  /** Account's Issued/Adjusted credit notes, offered as a funding source
   *  toward this period's rent due (Quick task 9 — Apply credit note). */
  creditNotes?: CreditNote[];
  /** Phase 38 (Member "Collect payment") — the member's OTHER non-Paid
   *  periods, oldest-first. When non-empty, each is marked fully paid (same
   *  payment mode/date/proof captured for the primary period) right after
   *  the primary period settles, all-or-report. Unset/empty → behavior is
   *  IDENTICAL to the pre-Phase-38 single-period dialog. */
  additionalPeriods?: PlanSchedulePeriod[];
  onClose: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const markPaid = useMarkPlanPeriodPaid();
  const updateReminder = useUpdatePlanPeriodReminder();
  const applyDeposit = useApplyDepositToPeriod();
  const applyCreditNote = useApplyCreditNoteToPeriod();
  // Local mirror of the period so an "Apply held deposit" / "Apply credit
  // note" click (a server round-trip that settles the source + records the
  // rent atomically) is reflected immediately in "Total to collect" /
  // balance math without closing the dialog.
  const [period, setPeriod] = useState<PlanSchedulePeriod>(initialPeriod);
  const [applyingDepositId, setApplyingDepositId] = useState<number | null>(null);
  const [applyingCreditNoteId, setApplyingCreditNoteId] = useState<number | null>(null);
  const deposit = round2(Math.max(0, firstPeriodDeposit ?? 0));
  // 2026-06-30 (GST/IT compliance): the input now captures the FULL bank
  // amount received (rent + deposit when applicable). The server splits:
  // rent → period.amountPaid, deposit portion → linked deposit row +
  // order-receipt PDF. Tax invoice continues to read only the rent portion.
  const remaining = round2(Math.max(0, period.amount - period.amountPaid));
  const totalToCollect = round2(remaining + deposit);

  // Phase 38 — aggregate outstanding across the primary period + every
  // additional period passed in (member Collect = full clear of ALL of the
  // member's non-Paid periods, not just the oldest). Purely additive/display;
  // the primary period's own bankAmount/amountPaid math below is unchanged.
  const additionalRemaining = round2(
    additionalPeriods.reduce((s, p) => s + Math.max(0, p.amount - p.amountPaid), 0),
  );
  const aggregateTotal = round2(totalToCollect + additionalRemaining);

  const [partial, setPartial] = useState(false);
  const [amount, setAmount] = useState<string>(String(totalToCollect > 0 ? totalToCollect : period.amount));
  const [mode, setMode] = useState<string>(period.paymentMode ?? "UPI");
  const [when, setWhen] = useState<string>(() => new Date().toISOString().slice(0, 16));
  const [proofUrl, setProofUrl] = useState<string | null>(period.paymentProofUrl ?? null);
  const [uploading, setUploading] = useState(false);
  // Next due date for the remaining balance (only relevant on a partial payment).
  const [nextDue, setNextDue] = useState<string>(period.reminderDate ?? "");

  function afterApply(updated: PlanSchedulePeriod) {
    setPeriod(updated);
    // Pre-fill the Bank-amount input with the reduced cash-to-collect so
    // the operator's next action is just confirming the smaller figure.
    const newRemaining = round2(Math.max(0, updated.amount - updated.amountPaid));
    const newTotal = round2(newRemaining + deposit);
    setAmount(String(newTotal > 0 ? newTotal : 0));
    queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
  }

  async function onApplyDeposit(depositId: number) {
    setApplyingDepositId(depositId);
    try {
      const updated = await applyDeposit.mutateAsync({
        id: accountId,
        depositId,
        data: { periodId: period.id },
      });
      afterApply(updated);
      toast({ title: "Held deposit applied toward rent due." });
    } catch (err) {
      // Structured error body lives on ApiError.data — see contacts-list.tsx precedent.
      const code = (err as { data?: { code?: string } })?.data?.code;
      toast({
        title: "Couldn't apply the deposit.",
        description: code === "nothing_due" ? "Nothing due on this period." : undefined,
        variant: "destructive",
      });
    } finally {
      setApplyingDepositId(null);
    }
  }

  async function onApplyCreditNote(creditNoteId: number) {
    setApplyingCreditNoteId(creditNoteId);
    try {
      const updated = await applyCreditNote.mutateAsync({
        id: creditNoteId,
        data: { periodId: period.id },
      });
      afterApply(updated);
      queryClient.invalidateQueries({ queryKey: getListAccountCreditNotesQueryKey(accountId) });
      toast({ title: "Credit note applied toward rent due." });
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      toast({
        title: "Couldn't apply the credit note.",
        description: code === "nothing_due" ? "Nothing due on this period." : undefined,
        variant: "destructive",
      });
    } finally {
      setApplyingCreditNoteId(null);
    }
  }

  // Live split preview of the bankAmount: rent first, then deposit.
  const entered = Math.max(0, Number(amount) || 0);
  const rentAllocated = Math.min(entered, remaining);
  const depositAllocated = Math.min(deposit, Math.max(0, entered - rentAllocated));
  // Balance left on the period (rent side) → drives the "next due date" field.
  const balanceAfter = Math.max(0, Math.round((remaining - rentAllocated) * 100) / 100);

  async function onProof(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadPaymentProof(file);
      if (r.ok) {
        setProofUrl(r.url);
        toast({ title: "Proof of payment uploaded." });
      } else {
        // Surface the real reason (mime/size rejected, not configured, 404, …)
        // instead of a generic failure so the operator can act on it.
        toast({
          title: "Couldn't upload proof",
          description: r.message || `Upload failed (HTTP ${r.status}).`,
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Couldn't upload proof",
        description: (err as Error)?.message ?? "Network error — check your connection.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    const thisPayment = Number(amount);
    if (!Number.isFinite(thisPayment) || thisPayment <= 0) {
      toast({ title: "Enter a valid amount.", variant: "destructive" });
      return;
    }
    // When the period carries a not-yet-actualized deposit, send the FULL
    // bank amount and let the server split (rent first, then deposit). For
    // subsequent periods (no deposit) the older amountPaid path works fine.
    const leftover = Math.max(0, Math.round((remaining - rentAllocated) * 100) / 100);
    try {
      const data: {
        bankAmount?: number;
        amountPaid?: number;
        paymentMode: string;
        paymentDate: string;
        paymentProofUrl: string | null;
      } =
        deposit > 0
          ? {
              bankAmount: thisPayment,
              paymentMode: mode,
              paymentDate: new Date(when).toISOString(),
              paymentProofUrl: proofUrl,
            }
          : {
              amountPaid: Math.min(
                period.amount,
                Math.round((period.amountPaid + thisPayment) * 100) / 100,
              ),
              paymentMode: mode,
              paymentDate: new Date(when).toISOString(),
              paymentProofUrl: proofUrl,
            };
      await markPaid.mutateAsync({
        periodId: period.id,
        data,
      });
      // Partial → record the next due date for the remaining balance (shown in
      // the schedule's Reminder Date column).
      if (leftover > 0 && nextDue) {
        await updateReminder.mutateAsync({ periodId: period.id, data: { reminderDate: nextDue } });
      }

      // Phase 38 — settle every additional pending period (member Collect =
      // clear the WHOLE outstanding, not just the oldest). Same payment
      // metadata captured above, full each, oldest-first (caller sorts),
      // all-or-report: stop and report progress on the first failure rather
      // than silently skipping the rest.
      let additionalSettled = 0;
      for (const extra of additionalPeriods) {
        try {
          await markPaid.mutateAsync({
            periodId: extra.id,
            data: {
              amountPaid: extra.amount,
              paymentMode: mode,
              paymentDate: new Date(when).toISOString(),
              paymentProofUrl: proofUrl,
            },
          });
          additionalSettled++;
        } catch {
          queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
          toast({
            title: "Cleared some, not all.",
            description: `Settled ${additionalSettled + 1} of ${additionalPeriods.length + 1} periods — the rest still need collecting.`,
            variant: "destructive",
          });
          onClose();
          return;
        }
      }

      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      const rentFullyPaid = period.amountPaid + rentAllocated >= period.amount;
      if (additionalPeriods.length > 0) {
        toast({ title: `Cleared ${additionalPeriods.length + 1} periods in full.` });
      } else {
        toast({
          title: rentFullyPaid
            ? depositAllocated > 0
              ? "Rent + deposit recorded."
              : "Marked fully paid."
            : "Partial payment recorded.",
        });
      }
      onClose();
    } catch (err) {
      toast({ title: "Couldn't record the payment.", description: apiErrorDetail(err), variant: "destructive" });
    }
  }

  async function markUnpaid() {
    try {
      await markPaid.mutateAsync({ periodId: period.id, data: { amountPaid: 0 } });
      queryClient.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      toast({ title: "Marked unpaid." });
      onClose();
    } catch {
      toast({ title: "Couldn't update.", variant: "destructive" });
    }
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>
            {(period.planName ?? "Plan").replace(/_/g, " ")} · {format(new Date(period.periodStart), "dd MMM yyyy")} → {format(new Date(period.periodEnd), "dd MMM yyyy")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Phase 38 — Member Collect payment: aggregate banner across ALL
              of this member's non-Paid periods (this one + additionalPeriods).
              Purely additive — only renders when the caller passed extra
              periods; the single-period account-detail.tsx flow never sees
              this block. */}
          {additionalPeriods.length > 0 && (
            <div className="space-y-1 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm">
              <div className="flex justify-between font-semibold">
                <span>Outstanding · {additionalPeriods.length + 1} periods</span>
                <span className="tabular-nums">{formatINR(aggregateTotal)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                This period {formatINR(totalToCollect)} + {additionalPeriods.length} more period(s) {formatINR(additionalRemaining)} — the payment mode/date/proof below settles ALL of them in full.
              </p>
            </div>
          )}

          {deposit > 0 ? (
            // First period: rent + one-time held deposit = the receipt's total.
            <div className="space-y-1 rounded-md border px-3 py-2 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Rent (this period)</span>
                <span className="tabular-nums">{formatINR(period.amount)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Security deposit (one-time, held)</span>
                <span className="tabular-nums">{formatINR(deposit)}</span>
              </div>
              <div className="flex justify-between border-t pt-1 font-semibold">
                <span>Total to collect</span>
                <span className="tabular-nums">{formatINR(totalToCollect)}</span>
              </div>
              {period.amountPaid > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  Rent already received: {formatINR(period.amountPaid)}
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-md border px-2 py-1.5">
                <p className="text-[11px] text-muted-foreground">Period total</p>
                <p className="font-semibold tabular-nums">{formatINR(period.amount)}</p>
              </div>
              <div className="rounded-md border px-2 py-1.5">
                <p className="text-[11px] text-muted-foreground">Already received</p>
                <p className="font-semibold tabular-nums">{formatINR(period.amountPaid)}</p>
              </div>
            </div>
          )}

          {/* Quick task 9 — apply a Held security deposit toward this
              period's rent due, reducing cash-to-collect. Only shown while
              rent remains outstanding; each deposit is applied in full
              (clamped server-side to the rent actually due). */}
          {remaining > 0 && heldDeposits.length > 0 && (
            <div className="space-y-1.5 rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2">
              <p className="text-xs font-medium text-sky-900">Apply a held deposit toward this due</p>
              {heldDeposits.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-muted-foreground">
                    Held deposit — {formatINR(d.amount)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={applyDeposit.isPending}
                    onClick={() => onApplyDeposit(d.id)}
                  >
                    {applyingDepositId === d.id ? "Applying…" : `Apply ₹${Math.round(d.amount)}`}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Quick task 9 — apply a WHOLE Issued/Adjusted credit note toward
              this period's rent due. No partial apply (operator-locked). */}
          {remaining > 0 && creditNotes.length > 0 && (
            <div className="space-y-1.5 rounded-md border border-violet-200 bg-violet-50/60 px-3 py-2">
              <p className="text-xs font-medium text-violet-900">Apply a credit note toward this due</p>
              {creditNotes.map((cn) => (
                <div key={cn.id} className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-muted-foreground truncate">
                    {cn.creditNoteNumber} — {formatINR(cn.totalReversed)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={applyCreditNote.isPending}
                    onClick={() => onApplyCreditNote(cn.id)}
                  >
                    {applyingCreditNoteId === cn.id ? "Applying…" : `Apply ₹${Math.round(cn.totalReversed)}`}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Phase 38 — Member Collect (full clear across all pending
              periods) does NOT offer partial payment; the Partial toggle
              only makes sense for a single period, so it's hidden whenever
              additionalPeriods is non-empty (forces full collection). */}
          {additionalPeriods.length === 0 && (
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div>
                <p className="text-sm font-medium">Partial payment</p>
                <p className="text-[11px] text-muted-foreground">
                  {partial
                    ? "Enter the bank amount received"
                    : `Receiving the full ${formatINR(totalToCollect)} together`}
                </p>
              </div>
              <Switch
                checked={partial}
                onCheckedChange={(v) => {
                  setPartial(v);
                  // Partial → clear the field so the operator enters the amount
                  // and the full ("complement") payment isn't recorded by default.
                  // Full → snap back to the total to collect (rent + deposit).
                  setAmount(v ? "" : String(totalToCollect > 0 ? totalToCollect : period.amount));
                }}
              />
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Bank amount received (₹)</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={!partial}
              placeholder={partial ? "Amount credited to bank" : ""}
              className="h-9"
            />
            {/* Live split preview — operator types ONE figure (matches bank),
                we show how it's allocated. Receipt PDF will mirror this:
                rent line + deposit line (refundable) + total. */}
            {deposit > 0 ? (
              <div className="text-[11px] text-muted-foreground space-y-0.5">
                <div className="flex justify-between">
                  <span>→ Rent + GST</span>
                  <span className="tabular-nums">{formatINR(rentAllocated)}</span>
                </div>
                <div className="flex justify-between">
                  <span>→ Security deposit (refundable)</span>
                  <span className="tabular-nums">{formatINR(depositAllocated)}</span>
                </div>
                {balanceAfter > 0 && (
                  <div className="flex justify-between text-amber-700">
                    <span>Rent balance after</span>
                    <span className="tabular-nums">{formatINR(balanceAfter)}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Balance after this: {formatINR(balanceAfter)}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Payment mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map((m) => (
                    <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date &amp; time</Label>
              <Input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="h-9"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Proof of payment</Label>
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm" disabled={uploading}>
                <label className="cursor-pointer">
                  {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
                  {proofUrl ? "Replace" : "Upload"}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => onProof(e.target.files?.[0])}
                  />
                </label>
              </Button>
              {proofUrl && (
                <a href={proofUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">
                  View uploaded proof
                </a>
              )}
            </div>
          </div>

          {/* Partial → when's the remaining balance due. */}
          {balanceAfter > 0 && (
            <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2">
              <Label className="text-xs">
                Next due date — balance of {formatINR(balanceAfter)}
              </Label>
              <Input
                type="date"
                value={nextDue}
                onChange={(e) => setNextDue(e.target.value)}
                className="h-9"
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {period.amountPaid > 0 && (
            <Button variant="ghost" size="sm" className="text-destructive sm:mr-auto" onClick={markUnpaid} disabled={markPaid.isPending}>
              Mark unpaid
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={markPaid.isPending || uploading || !(Number(amount) > 0)}>
            {markPaid.isPending ? "Saving…" : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
